import React, { FC, useMemo } from 'react';
import PurchaseOrderStatusDisplay from '../purchase_order_status_display/PurchaseOrderStatusDisplay';
import {
	getCurrency,
	getPurchaseOrderId,
	nullSafeGet,
	nullSafeGetOrElse,
} from '../../utils/DataAccessUtils';
import moment from 'moment';
import DateTimeHover from '../date_time_component/DateTime';
import { Tag } from 'antd';
import PastDue from '../work_order_row_display_simple/PastDue';
import { isDatePastDue } from '../../utils/DataFormatterUtils';

require('./PurchaseOrderRowDisplay.less');

const PartEquipmentFirstLineDisplay: FC<any> = ({ purchaseOrder }) => {
	const totalCount = useMemo(
		() =>
			nullSafeGetOrElse('partSupplierPurchaseOrderLineItems', purchaseOrder, []).length +
			nullSafeGetOrElse('equipmentSupplierPurchaseOrderLineItems', purchaseOrder, []).length +
			nullSafeGetOrElse('spotItemSupplierPurchaseOrderLineItems', purchaseOrder, []).length,
		[purchaseOrder]
	);

	const displayText = useMemo(() => {
		const partLineItmes = nullSafeGetOrElse(
			'partSupplierPurchaseOrderLineItems',
			purchaseOrder,
			[]
		);
		const equipmentLineItems = nullSafeGetOrElse(
			'equipmentSupplierPurchaseOrderLineItems',
			purchaseOrder,
			[]
		);
		const spotItemLineItems = nullSafeGetOrElse(
			'spotItemSupplierPurchaseOrderLineItems',
			purchaseOrder,
			[]
		);
		if (partLineItmes.length > 0) {
			const record = partLineItmes[0];
			return `${nullSafeGetOrElse('partQuantity', record, '--')} ${
				nullSafeGetOrElse('partQuantity', record, 0) > 1
					? nullSafeGet('partUom.displayNamePlural', record)
					: nullSafeGet('partUom.displayNameSingular', record)
			} of ${nullSafeGetOrElse('part.name', record, '')}`;
		} else if (equipmentLineItems.length > 0) {
			const record = equipmentLineItems[0];
			return `${nullSafeGetOrElse('equipmentQuantity', record, 0)} of ${nullSafeGetOrElse(
				'equipmentType.modelName',
				record,
				''
			)}`;
		} else if (spotItemLineItems.length > 0) {
			const record = spotItemLineItems[0];
			return `${nullSafeGetOrElse('spotItemQuantity', record, 0)} of ${nullSafeGetOrElse(
				'spotItemName',
				record,
				''
			)}`;
		} else return '';
	}, [purchaseOrder]);

	return displayText ? (
		<div>
			<span>{displayText}</span>
			{totalCount > 1 ? (
				<span className="additional-detail ml-2">{`+${totalCount - 1} more`}</span>
			) : null}
		</div>
	) : null;
};

const PurchaseOrderRowDisplay: FC<any> = ({ purchaseOrder, ...props }) => {
	const currency = getCurrency({
		currencyId: nullSafeGet('currencyId', purchaseOrder),
		supplierFacility: nullSafeGet('supplierFacility', purchaseOrder),
	});
	return (
		<div className="flex items-center justify-between" {...props}>
			<div className="purchaseOrderRowDisplay flex items-center justify-between">
				<div className="flex flex-col">
					<div className="mb-3 flex flex-row items-center">
						<div className="purchaseOrderRowDisplay__rowTitle">
							Purchase Order {getPurchaseOrderId(purchaseOrder)}
						</div>
					</div>
					<div className="purchaseOrderRowDisplay__rowSubtitle">
						{nullSafeGet('partEquipmentVendor.name', purchaseOrder) ? (
							<div
								className="purchaseOrderRowDisplay__rowInlineGroup"
								style={{ display: 'inline-flex' }}
							>
								<i className="icons8-font icons8-company mr-2" />
								{nullSafeGet('partEquipmentVendor.name', purchaseOrder)}
							</div>
						) : null}
						<div className="purchaseOrderRowDisplay__rowInlineGroup">
							<span>
								Created{' '}
								<DateTimeHover
									timestamp={nullSafeGet('createdAt', purchaseOrder)}
									showTime={false}
								/>
							</span>
						</div>
						{nullSafeGet('integratorName', purchaseOrder) ? (
							<div className="purchaseOrderRowDisplay__rowInlineGroup">
								<Tag color="magenta">{nullSafeGet('integratorName', purchaseOrder)}</Tag>
							</div>
						) : null}
						<div
							className="purchaseOrderRowDisplay__rowInlineGroup"
							style={{ display: 'inline-flex' }}
						>
							<PurchaseOrderStatusDisplay status={purchaseOrder.status} />
						</div>
					</div>
					<div className="purchaseOrderRowDisplay__rowInlineGroup mt-2">
						<PartEquipmentFirstLineDisplay purchaseOrder={purchaseOrder} />
					</div>
					{nullSafeGet('dueDate', purchaseOrder) &&
					isDatePastDue(nullSafeGet('dueDate', purchaseOrder)) ? (
						<div className="mt-2 flex flex-row items-center">
							<PastDue
								dueDate={nullSafeGet('dueDate', purchaseOrder)}
								timeZoneId={
									nullSafeGet('location.timeZoneId', purchaseOrder) ||
									nullSafeGet('stockLocation.location.timeZoneId', purchaseOrder)
								}
								showTime={false}
							/>
						</div>
					) : null}
				</div>
			</div>
			<div>{currency.format(nullSafeGetOrElse('totalCost', purchaseOrder, 0))}</div>
		</div>
	);
};

export default PurchaseOrderRowDisplay;
